// /bendahara/pengesahan — Antrean pengesahan bukti bayaran (Prompt D)
// Alur: pilih bukti -> pratonton resit (anggaran no) -> Sahkan & Terbitkan Resit
//       -> PDF di-upload ke Drive -> resit terbit (no dikunci)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, ZoomIn, ZoomOut, FileText, Loader2, Printer, Download, RefreshCw, AlertTriangle, MessageCircle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { formatRM } from '../../lib/format';
import { waLink as buildWaLink } from '../../lib/wa';
import { driveObjectUrl, revokeObjectUrl, uploadResitPdf } from '../../lib/drive';
import { resitPdfBlob, downloadBlob } from '../../lib/resitPdf';
import PageHeader from '../../components/PageHeader';
import StatusPill from '../../components/StatusPill';
import EmptyState from '../../components/EmptyState';
import { CardSkeleton } from '../../components/Skeletons';
import ResitView, { RESIT_CSS } from '../../components/ResitView';
import { CenterMorphModal, CenterMorphModalContent } from '../../components/beui/CenterMorphModal';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';

const KAEDAH_OPTS = [
  { v: 'transfer', l: 'Pindahan Bank' },
  { v: 'qr_manual', l: 'QR Manual' },
  { v: 'tunai', l: 'Tunai' },
];

function fmtTarikh(s) {
  return new Date(s).toLocaleString('ms-MY', { dateStyle: 'medium', timeStyle: 'short' });
}

// SLA: bukti 'pending' lebih dari 24 jam dianggap lewat
const SLA_MS = 24 * 3600 * 1000;
function slaOverdue(p) {
  return p.status === 'pending' && (Date.now() - new Date(p.created_at).getTime()) > SLA_MS;
}
function slaHours(p) {
  return Math.floor((Date.now() - new Date(p.created_at).getTime()) / 3600000);
}

// Nombor WA ibu/bapa: telefon pengupload (profil) dahulu, kemudian telefon_orangtua murid.
function parentPhoneOf(p, maps) {
  return (maps.uploaderPhones && maps.uploaderPhones[p.diupload_oleh])
    || (maps.students[p.student_id] && maps.students[p.student_id].telefon_orangtua)
    || '';
}

export default function Pengesahan() {
  const { t, i18n } = useTranslation();
  const { profile, school } = useAuth();
  const [tab, setTab] = useState('menunggu');
  const [loading, setLoading] = useState(true);
  const [proofs, setProofs] = useState([]);
  const [maps, setMaps] = useState({ students: {}, classes: {}, invoices: {}, tariffs: {}, profiles: {}, paid: {}, proofInvs: {} });
  const [selected, setSelected] = useState(null);   // proof detail
  const [preview, setPreview] = useState(null);     // { proof, noAnggaran, kaedah, mode }
  const [rejecting, setRejecting] = useState(null); // proof
  const [alasan, setAlasan] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [issued, setIssued] = useState(null);       // resit terbit { resit, pdfWarn }
  const [zoom, setZoom] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data: pr, error } = await supabase.from('payment_proofs')
        .select('id, invoice_id, student_id, drive_file_id, nama_file, ukuran_kb, status, alasan_tolak, diupload_oleh, dibuka_bendahara_at, created_at')
        .order('created_at', { ascending: false }).limit(300);
      if (error) throw error;
      const list = pr || [];
      const sIds = [...new Set(list.map((p) => p.student_id).filter(Boolean))];
      const pIds = list.map((p) => p.id);
      const piR = pIds.length
        ? await supabase.from('proof_invoices').select('proof_id, invoice_id').in('proof_id', pIds)
        : { data: [] };
      const proofInvs = {};
      (piR.data || []).forEach((r) => { (proofInvs[r.proof_id] = proofInvs[r.proof_id] || []).push(r.invoice_id); });
      const iIds = [...new Set([
        ...list.map((p) => p.invoice_id).filter(Boolean),
        ...(piR.data || []).map((r) => r.invoice_id),
      ])];
      const uIds = [...new Set(list.map((p) => p.diupload_oleh).filter(Boolean))];
      const [sR, iR, uR, pR] = await Promise.all([
        sIds.length ? supabase.from('students').select('id, nama, class_id, telefon_orangtua').in('id', sIds) : { data: [] },
        iIds.length ? supabase.from('invoices').select('id, jumlah_bayar_rm, diskon_rm, diskon_nota, status, due_date, tariff_id, student_id').in('id', iIds) : { data: [] },
        uIds.length ? supabase.from('profiles').select('id, nama, telefon').in('id', uIds) : { data: [] },
        iIds.length ? supabase.from('payments').select('invoice_id, jumlah_rm').in('invoice_id', iIds).eq('voided', false) : { data: [] },
      ]);
      const students = Object.fromEntries((sR.data || []).map((s) => [s.id, s]));
      const cIds = [...new Set(Object.values(students).map((s) => s.class_id).filter(Boolean))];
      const cR = cIds.length ? await supabase.from('classes').select('id, nama').in('id', cIds) : { data: [] };
      const classes = Object.fromEntries((cR.data || []).map((c) => [c.id, c.nama]));
      const invoices = Object.fromEntries((iR.data || []).map((i) => [i.id, i]));
      const tIds = [...new Set((iR.data || []).map((i) => i.tariff_id).filter(Boolean))];
      const tR = tIds.length ? await supabase.from('tariffs').select('id, nama').in('id', tIds) : { data: [] };
      const tariffs = Object.fromEntries((tR.data || []).map((x) => [x.id, x.nama]));
      const profiles = Object.fromEntries((uR.data || []).map((x) => [x.id, x.nama]));
      const uploaderPhones = Object.fromEntries((uR.data || []).map((x) => [x.id, x.telefon || '']));
      const paid = {};
      (pR.data || []).forEach((p) => { paid[p.invoice_id] = (paid[p.invoice_id] || 0) + Number(p.jumlah_rm); });
      setProofs(list);
      setMaps({ students, classes, invoices, tariffs, profiles, uploaderPhones, paid, proofInvs });
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Muat pratonton fail bukti bila dialog detail dibuka
  useEffect(() => {
    let alive = true; let url = null;
    (async () => {
      setFileUrl(null);
      if (!selected?.drive_file_id) return;
      try {
        const r = await driveObjectUrl(selected.drive_file_id);
        if (!alive) { revokeObjectUrl(r.url); return; }
        url = r.url; setFileUrl({ ...r });
      } catch (e) { if (alive) setErr(e.message); }
    })();
    return () => { alive = false; if (url) revokeObjectUrl(url); };
  }, [selected]);

  const pending = useMemo(() => proofs.filter((p) => p.status === 'pending'), [proofs]);
  const history = useMemo(() => proofs.filter((p) => p.status !== 'pending'), [proofs]);
  const overdue = useMemo(() => pending.filter(slaOverdue), [pending]);
  const rows = tab === 'menunggu' ? pending : history;

  // Tandai "dibuka bendahara" saat dialog rincian dibuka (untuk progress tracker ortu)
  useEffect(() => {
    if (!selected || selected.dibuka_bendahara_at || selected.status !== 'pending') return;
    const now = new Date().toISOString();
    setProofs((xs) => xs.map((p) => (p.id === selected.id ? { ...p, dibuka_bendahara_at: now } : p)));
    setSelected((s) => (s && s.id === selected.id ? { ...s, dibuka_bendahara_at: now } : s));
    supabase.from('payment_proofs').update({ dibuka_bendahara_at: now }).eq('id', selected.id)
      .then(({ error }) => { if (error) console.warn('dibuka_bendahara_at:', error.message); });
  }, [selected]);

  // Semua invois terpaut satu bukti (jadual perantara; fallback invoice_id lama)
  const invoicesOf = (proof) => {
    const ids = (maps.proofInvs[proof.id] && maps.proofInvs[proof.id].length)
      ? maps.proofInvs[proof.id]
      : (proof.invoice_id ? [proof.invoice_id] : []);
    return ids.map((id) => maps.invoices[id]).filter(Boolean);
  };

  const bakiOf = (proof) => {
    const invs = invoicesOf(proof);
    if (!invs.length) return null;
    return invs.reduce((a, inv) => a + Math.max(0, Number(inv.jumlah_bayar_rm) - (maps.paid[inv.id] || 0)), 0);
  };

  // Label ringkas tempoh: "Jan, Feb 2026" / "2026"
  const periodLabel = (invs) => {
    const loc = i18n.language === 'en' ? 'en-MY' : i18n.language === 'id' ? 'id-ID' : 'ms-MY';
    const labels = invs.map((inv) => {
      const mt = inv.periode && String(inv.periode).match(/^(\d{4})-(0[1-9]|1[0-2])$/);
      if (mt) return { y: mt[1], m: new Intl.DateTimeFormat(loc, { month: 'short' }).format(new Date(+mt[1], +mt[2] - 1, 1)) };
      return { y: null, m: String(inv.periode || '') };
    });
    const years = [...new Set(labels.map((l) => l.y).filter(Boolean))];
    if (years.length === 1) return `${labels.map((l) => l.m).join(', ')} ${years[0]}`;
    return labels.map((l) => (l.y ? `${l.m} ${l.y}` : l.m)).join(', ');
  };

  const openPreview = async (proof) => {
    setBusy(true); setErr('');
    try {
      const { data, error } = await supabase.rpc('preview_receipt_no', {
        p_school: school.id, p_tahun: new Date().getFullYear(),
      });
      if (error) throw error;
      setPreview({ proof, noAnggaran: data, kaedah: 'transfer', mode: 'thermal' });
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const doApprove = async () => {
    if (!preview) return;
    setBusy(true); setErr('');
    try {
      const { data, error } = await supabase.rpc('approve_proof', {
        p_proof_id: preview.proof.id, p_kaedah: preview.kaedah,
      });
      if (error) throw error;
      // Satu resit bagi setiap invois (multi-bulan -> beberapa resit)
      const items = (data.invoices && data.invoices.length) ? data.invoices : [data];
      const list = [];
      let pdfWarn = '';
      for (const it of items) {
        const resit = {
          no_resit: it.no_resit,
          tanggal: it.tanggal,
          studentNama: data.student_nama,
          kelas: data.kelas_nama,
          tarifNama: it.tarif_nama || data.tarif_nama,
          diskonNota: it.diskon_nota,
          amount: Number(it.amount),
          kaedah: data.kaedah,
          penyemak: profile?.nama,
          baki: Number(it.baki),
        };
        // Jana PDF & upload ke Drive (kegagalan upload tidak membatalkan resit)
        try {
          const blob = await resitPdfBlob({ resit, school });
          await uploadResitPdf({ blob, receiptId: it.receipt_id, filename: `resit-${it.no_resit.replace(/\//g, '-')}.pdf` });
        } catch (e) { pdfWarn = e.message; }
        list.push({ resit, receiptId: it.receipt_id });
      }
      const waPhoneP = parentPhoneOf(preview.proof, maps);
      const waNamaP = maps.profiles[preview.proof.diupload_oleh] || data.student_nama || '';
      list.forEach((it) => { it.waPhone = waPhoneP; it.waNama = waNamaP; });
      setPreview(null); setSelected(null);
      setIssued({ list, pdfWarn });
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const doReject = async () => {
    if (!rejecting || !alasan.trim()) return;
    setBusy(true); setErr('');
    try {
      const { error } = await supabase.rpc('reject_proof', { p_proof_id: rejecting.id, p_alasan: alasan.trim() });
      if (error) throw error;
      setRejecting(null); setAlasan(''); setSelected(null);
      load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const printIssued = (m) => {
    const el = document.getElementById('issued-resit-style');
    const css = m === 'thermal' ? '@page{size:80mm auto;margin:3mm}' : '@page{size:A4;margin:14mm}';
    if (el) el.innerHTML = css;
    setTimeout(() => window.print(), 80);
  };

  const [dlOne, setDlOne] = useState(null);
  const downloadOne = async (it) => {
    setDlOne(it.receiptId);
    try {
      downloadBlob(await resitPdfBlob({ resit: it.resit, school }), `resit-${it.resit.no_resit.replace(/\//g, '-')}.pdf`);
    } catch (e) { setErr(e.message); }
    setDlOne(null);
  };

  if (loading) return <div className="p-6"><CardSkeleton /><CardSkeleton /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <style>{RESIT_CSS}</style>
      <style id="issued-resit-style">{'@page{size:80mm auto;margin:3mm}'}</style>
      <PageHeader title={t('pengesahan')} subtitle={t('pengesahanSub')} />

      {err && <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-4 py-3 text-sm text-red-700 dark:text-red-300"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{err}</div>}

      <div className="flex gap-2 mb-5">
        {['menunggu', 'sejarah'].map((k) => (
          <button key={k} onClick={() => setTab(k)}
            className={`h-10 px-4 rounded-xl text-sm font-semibold transition-colors ${tab === k ? 'bg-brand text-white' : 'bg-card border border-border text-muted-foreground hover:bg-muted'}`}>
            {t(k === 'menunggu' ? 'menunggu' : 'sejarah')}
            {k === 'menunggu' && pending.length > 0 && (
              <Badge className="ml-2 bg-amber-400 text-amber-950 hover:bg-amber-400">{pending.length}</Badge>
            )}
          </button>
        ))}
        <button onClick={load} className="ml-auto h-10 w-10 flex items-center justify-center rounded-xl bg-card border border-border text-muted-foreground hover:bg-muted" title={t('muatSemula')}>
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {tab === 'menunggu' && overdue.length > 0 && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-4">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200 font-medium">{t('slaBanner', { n: overdue.length })}</p>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState icon={CheckCircle2} title={t(tab === 'menunggu' ? 'tiadaPending' : 'tiadaSejarah')} />
      ) : (
        <div className="grid gap-3">
          {rows.map((p) => {
            const s = maps.students[p.student_id] || {};
            const invs = invoicesOf(p);
            const baki = bakiOf(p);
            return (
              <button key={p.id} onClick={() => { setSelected(p); setZoom(false); }}
                className="text-left bg-card border border-border rounded-2xl p-4 hover:border-brand/50 hover:shadow-sm transition-all flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-foreground truncate">{s.nama || '—'}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {maps.classes[s.class_id] || '-'} · {invs.length > 1 ? t('bilDipilih', { n: invs.length }) : (maps.tariffs[invs[0]?.tariff_id] || t('bil'))} · {periodLabel(invs)}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{t('dimuatNaikOleh')}: {maps.profiles[p.diupload_oleh] || '-'} · {fmtTarikh(p.created_at)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-bold text-foreground">{baki == null ? '—' : formatRM(baki)}</p>
                  <StatusPill status={p.status} />
                  {slaOverdue(p) && (
                    <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900 px-2 py-0.5 text-[11px] font-bold text-red-700 dark:text-red-300">
                      <AlertTriangle className="h-3 w-3" />{t('slaLewat', { n: slaHours(p) })}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Dialog detail bukti */}
      <CenterMorphModal open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <CenterMorphModalContent ariaLabel={t('semakBukti')} className="max-w-3xl bg-card">
          <div className="p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{t('semakBukti')}</h2>
            </div>
          {selected && (() => {
            const p = selected;
            const s = maps.students[p.student_id] || {};
            const invs = invoicesOf(p);
            const baki = bakiOf(p);
            return (
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-muted-foreground">{p.nama_file || 'bukti'}</span>
                    <button onClick={() => setZoom((z) => !z)} className="h-9 w-9 flex items-center justify-center rounded-lg border border-border hover:bg-muted" title={t('zum')}>
                      {zoom ? <ZoomOut className="h-4 w-4" /> : <ZoomIn className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="rounded-xl border border-border bg-muted overflow-auto max-h-[420px] flex items-start justify-center">
                    {fileUrl ? (
                      fileUrl.mime === 'application/pdf'
                        ? <iframe src={fileUrl.url} title="bukti" className="w-full h-[420px]" />
                        : <img src={fileUrl.url} alt="bukti bayaran" className={`transition-transform ${zoom ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'} max-w-none`} style={zoom ? {} : { maxWidth: '100%' }} onClick={() => setZoom((z) => !z)} />
                    ) : <div className="p-10 text-muted-foreground text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t('memuat')}</div>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">{t('klikZum')}</p>
                </div>
                <div className="space-y-3 text-sm">
                  <InfoRow l={t('pelajar')} v={s.nama} />
                  <InfoRow l={t('kelas')} v={maps.classes[s.class_id] || '-'} />
                  <div>
                    <p className="text-muted-foreground text-sm mb-1.5">{t('bil')} ({invs.length})</p>
                    <div className="space-y-1.5 rounded-xl bg-muted p-3">
                      {invs.map((inv) => {
                        const ib = Math.max(0, Number(inv.jumlah_bayar_rm) - (maps.paid[inv.id] || 0));
                        return (
                          <div key={inv.id} className="flex items-center justify-between gap-2 text-xs">
                            <span className="text-muted-foreground min-w-0 truncate">
                              {maps.tariffs[inv.tariff_id] || 'Yuran'} · {periodLabel([inv])}{inv.diskon_nota ? ` (${inv.diskon_nota})` : ''}
                            </span>
                            <span className="font-bold font-mono tabular text-foreground shrink-0">{formatRM(ib)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <InfoRow l={t('jumlahPerluDibayar')} v={baki == null ? '—' : formatRM(baki)} bold />
                  <InfoRow l={t('dimuatNaikOleh')} v={maps.profiles[p.diupload_oleh] || '-'} />
                  <InfoRow l={t('tarikhMuatNaik')} v={fmtTarikh(p.created_at)} />
                  {p.status === 'tolak' && p.alasan_tolak && <InfoRow l={t('alasanTolak')} v={p.alasan_tolak} />}
                  {p.status === 'tolak' && (() => {
                    const href = buildWaLink(parentPhoneOf(p, maps), t('waTolak', {
                      nama: maps.profiles[p.diupload_oleh] || s.nama, anak: s.nama,
                      bil: periodLabel(invs), alasan: p.alasan_tolak || '-',
                    }));
                    return href && (
                      <a href={href} target="_blank" rel="noreferrer"
                        className="flex h-11 items-center justify-center gap-2 rounded-xl bg-emerald-600 text-sm font-semibold text-white hover:bg-emerald-700">
                        <MessageCircle className="h-4 w-4" />{t('hantarWA')}
                      </a>
                    );
                  })()}
                  {p.status === 'pending' && (
                    <div className="flex gap-2 pt-2">
                      <Button onClick={() => openPreview(p)} disabled={busy} className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" />{t('luluskan')}
                      </Button>
                      <Button onClick={() => setRejecting(p)} disabled={busy} variant="destructive" className="flex-1 h-11">
                        <XCircle className="h-4 w-4 mr-1.5" />{t('tolak')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>

      {/* Dialog pratonton resit sebelum sahkan */}
      <CenterMorphModal open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <CenterMorphModalContent ariaLabel={t('pratontonResit')} className="max-w-md bg-card">
          <div className="p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{t('pratontonResit')}</h2>
            </div>
          {preview && (() => {
            const p = preview.proof;
            const s = maps.students[p.student_id] || {};
            const invs = invoicesOf(p);
            const first = invs[0] || {};
            const bakiFirst = first.id ? Math.max(0, Number(first.jumlah_bayar_rm) - (maps.paid[first.id] || 0)) : 0;
            const resit = {
              no_resit: `${preview.noAnggaran}*`,
              tanggal: new Date(),
              studentNama: s.nama, kelas: maps.classes[s.class_id] || '-',
              tarifNama: maps.tariffs[first.tariff_id] || 'Yuran',
              diskonNota: first.diskon_nota,
              amount: bakiFirst, kaedah: preview.kaedah,
              penyemak: profile?.nama, baki: 0,
            };
            return (
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">* {t('noAnggaranNota')}</p>
                {invs.length > 1 && (
                  <div className="rounded-xl bg-muted p-3">
                    <p className="text-xs font-bold text-muted-foreground mb-1.5">{t('bilDipilih', { n: invs.length })}</p>
                    <div className="space-y-1.5">
                      {invs.map((inv) => (
                        <div key={inv.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="text-muted-foreground truncate">{maps.tariffs[inv.tariff_id] || 'Yuran'} · {periodLabel([inv])}</span>
                          <span className="font-bold font-mono tabular text-foreground shrink-0">{formatRM(Math.max(0, Number(inv.jumlah_bayar_rm) - (maps.paid[inv.id] || 0)))}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">{t('resitSetiapBil')}</p>
                  </div>
                )}
                <div className="flex gap-2">
                  {['thermal', 'a4'].map((m) => (
                    <button key={m} onClick={() => setPreview({ ...preview, mode: m })}
                      className={`flex-1 h-10 rounded-xl text-sm font-semibold border ${preview.mode === m ? 'border-brand bg-brand-light text-brand' : 'border-border text-muted-foreground'}`}>
                      {m === 'thermal' ? '80mm' : 'A4'}
                    </button>
                  ))}
                </div>
                <ResitView resit={resit} school={school} mode={preview.mode} estimate />
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">{t('kaedahBayaran')}</label>
                  <div className="flex gap-2 mt-1">
                    {KAEDAH_OPTS.map((o) => (
                      <button key={o.v} onClick={() => setPreview({ ...preview, kaedah: o.v })}
                        className={`flex-1 h-10 rounded-xl text-xs font-semibold border ${preview.kaedah === o.v ? 'border-brand bg-brand-light text-brand' : 'border-border text-muted-foreground'}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => setPreview(null)} disabled={busy} className="flex-1 h-11">{t('batal')}</Button>
            <Button onClick={doApprove} disabled={busy} className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              {t('sahkanTerbit')}
            </Button>
          </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>

      {/* Dialog tolak */}
      <CenterMorphModal open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <CenterMorphModalContent ariaLabel={t('tolakBukti')} className="max-w-md bg-card">
          <div className="p-6">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{t('tolakBukti')}</h2>
            </div>
          <p className="text-sm text-muted-foreground">{t('alasanTolakWajib')}</p>
          <Textarea value={alasan} onChange={(e) => setAlasan(e.target.value)} rows={4}
            placeholder={t('contohAlasan')} className="mt-2" />
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => { setRejecting(null); setAlasan(''); }} disabled={busy} className="flex-1 h-11">{t('batal')}</Button>
            <Button onClick={doReject} disabled={busy || !alasan.trim()} variant="destructive" className="flex-1 h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}{t('tolak')}
            </Button>
          </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>

      {/* Dialog resit terbit */}
      <CenterMorphModal open={!!issued} onOpenChange={(o) => !o && setIssued(null)}>
        <CenterMorphModalContent ariaLabel={t('resitTerbit')} className="max-w-md bg-card">
          <div className="p-6 max-h-[85vh] overflow-y-auto">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-emerald-700 dark:text-emerald-300 flex items-center gap-2"><CheckCircle2 className="h-5 w-5" />{t('resitTerbit')}</h2>
            </div>
          {issued?.pdfWarn && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{t('pdfGagal')}: {issued.pdfWarn}
            </div>
          )}
          {issued && issued.list.map((it, i) => (
            <div key={it.receiptId} className={i < issued.list.length - 1 ? 'mb-6 pb-6 border-b border-dashed border-border print:mb-0 print:pb-0 print:border-0 print:break-after-page' : ''}>
              <ResitView resit={it.resit} school={school} mode="thermal" />
              <div className="no-print mt-2 flex justify-end gap-2">
                {(() => {
                  const href = buildWaLink(it.waPhone, t('waLulus', {
                    nama: it.waNama || it.resit.studentNama, anak: it.resit.studentNama,
                    bil: it.resit.tarifNama, jumlah: formatRM(it.resit.amount), resit: it.resit.no_resit,
                  }));
                  return href && (
                    <a href={href} target="_blank" rel="noreferrer"
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700">
                      <MessageCircle className="h-3.5 w-3.5" />{t('hantarWA')}
                    </a>
                  );
                })()}
                <Button size="sm" variant="outline" onClick={() => downloadOne(it)} disabled={dlOne === it.receiptId}>
                  {dlOne === it.receiptId ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
                  PDF · {it.resit.no_resit}
                </Button>
              </div>
            </div>
          ))}
          <div className="flex gap-2 no-print mt-4">
            <Button onClick={() => printIssued('thermal')} className="flex-1 h-11 bg-brand text-white hover:bg-brand-royal"><Printer className="h-4 w-4 mr-1.5" />{t('cetak80')}</Button>
            <Button onClick={() => setIssued(null)} variant="ghost" className="h-11">{t('tutup')}</Button>
          </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>
    </div>
  );
}

function InfoRow({ l, v, bold }) {
  return (
    <div className="flex justify-between gap-3 border-b border-border pb-2">
      <span className="text-muted-foreground">{l}</span>
      <span className={`text-right ${bold ? 'font-bold text-foreground' : 'font-medium text-foreground'}`}>{v || '—'}</span>
    </div>
  );
}
